import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Chip,
  Stack,
  Divider
} from '@mui/material';

type Recipe = {
  recipeName?: string;
  title?: string;
  recipeImage?: string;
  image?: string;
  ingredients?: string[];
  instructions?: string | string[];
  recipeStub?: any;
  servings?: number;
  nutrition?: any;
  readyInMinutes?: number;
  sourceUrl?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  recipe: Recipe | null;
}

const RecipeModal: React.FC<Props> = ({ open, onClose, recipe }) => {
  if (!recipe) return null;

  const title = recipe.recipeName || recipe.title || 'Recipe';
  const image = recipe.recipeImage || recipe.image || recipe.recipeStub?.image;
  const ingredients: string[] = recipe.ingredients || recipe.recipeStub?.ingredients || [];
  let instructions: string[] = [];

  if (Array.isArray(recipe.instructions)) {
    instructions = recipe.instructions as string[];
  } else if (typeof recipe.instructions === 'string') {
    instructions = (recipe.instructions as string).split('\n').filter(Boolean);
  } else if (recipe.recipeStub?.analyzedInstructions?.[0]?.steps) {
    instructions = recipe.recipeStub.analyzedInstructions[0].steps.map((s: any) => s.step);
  } else if (recipe.recipeStub?.instructions && typeof recipe.recipeStub.instructions === 'string') {
    instructions = recipe.recipeStub.instructions.split('\n').filter(Boolean);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
          {image && (
            <Box flex="0 0 260px" display="flex" justifyContent="center" alignItems="center">
              <Avatar src={image} variant="rounded" sx={{ width: 260, height: 160 }} />
            </Box>
          )}

          <Box flex="1">
            <Stack direction="row" spacing={1} mb={1} flexWrap="wrap">
              <Chip label={`${recipe.recipeStub?.readyInMinutes || recipe.readyInMinutes || 'N/A'} min`} />
              <Chip label={`Servings: ${recipe.servings || recipe.recipeStub?.servings || 'N/A'}`} />
              <Chip label={`Calories: ${recipe.recipeStub?.nutrition?.calories || recipe.nutrition?.calories || 'N/A'}`} />
            </Stack>

            <Divider sx={{ mb: 1 }} />

            <Typography variant="h6" gutterBottom>
              Ingredients
            </Typography>
            {ingredients && ingredients.length > 0 ? (
              <Box component="ul" sx={{ pl: 3, mb: 1 }}>
                {ingredients.map((ing, i) => (
                  <li key={i}>
                    <Typography variant="body2">{ing}</Typography>
                  </li>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" mb={1}>
                No ingredients available.
              </Typography>
            )}

            <Divider sx={{ my: 1 }} />

            <Typography variant="h6" gutterBottom>
              Instructions
            </Typography>
            {instructions && instructions.length > 0 ? (
              <Box component="ol" sx={{ pl: 3 }}>
                {instructions.map((ins, i) => (
                  <li key={i}>
                    <Typography variant="body2" paragraph>
                      {ins}
                    </Typography>
                  </li>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No instructions available.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        {recipe.sourceUrl && (
          <Button component="a" href={recipe.sourceUrl} target="_blank" rel="noopener noreferrer">
            Open Source
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeModal;
